import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCommentDots,
  faTimes,
  faPaperPlane,
  faShieldAlt,
  faArrowRight,
  faPlus,
  faEnvelope,
  faGraduationCap,
  faBell,
  faQuestionCircle,
  faUser,
  faVolumeUp,
  faVolumeMute,
  faVolumeOff,
} from "@fortawesome/free-solid-svg-icons";
import ChatAPI from "./services/ChatAPI";
import TTSService from "./services/TTSService";
import DPGMascot from "./DPGMascot";
import dpgPersonality from "./DPGPersonality";
import { useUser } from "./context/UserContext";
import useFeatureAccess from "../../hooks/useFeatureAccess";
import { useTranslation } from "../../i18n/LanguageContext";
import "./css/ChatWidget.css";

/* ── Helpers (no hooks, no side-effects) ───────────────────────── */

/** Safely read / write localStorage (Safari private-browsing guard) */
const safeStorage = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, val) {
    try {
      localStorage.setItem(key, val);
    } catch {
      /* noop */
    }
  },
};

/**
 * ChatWidget — Floating chatbot assistant for KinShield.
 *
 * Props:
 *  - setHeroContent: function to navigate to a page/section in the app
 *  - currentPage: string  — the current section the user is viewing
 */
function ChatWidget({ setHeroContent, currentPage }) {
  const { loggedInUser } = useUser();
  const { isDeactivated } = useFeatureAccess();
  const { t, language } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(() => {
    const saved = safeStorage.get("dpg-tts-enabled");
    if (saved !== null) return saved === "true";
    try {
      return (
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
        window.innerWidth < 768
      );
    } catch {
      return false;
    }
  });
  const [speakingMsgId, setSpeakingMsgId] = useState(null);
  const [mascotMood, setMascotMood] = useState("idle");
  const [nudgeBubble, setNudgeBubble] = useState(null);
  const [nudgeVisible, setNudgeVisible] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const nudgeTimerRef = useRef(null);
  const idleTimerRef = useRef(null);

  // Reset conversation when language changes so stored messages don't remain
  // in the previous language.
  const prevLanguageRef = useRef(language);
  useEffect(() => {
    if (prevLanguageRef.current !== language) {
      prevLanguageRef.current = language;
      setMessages([]);
      setConversationId(null);
      setQuickReplies([]);
    }
  }, [language]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Cancel speech when chat closes
  useEffect(() => {
    if (!isOpen) {
      TTSService.stop();
      setSpeakingMsgId(null);
    }
  }, [isOpen]);

  // Clean up speech on unmount
  useEffect(() => {
    return () => {
      TTSService.stop();
    };
  }, []);

  // ─── Mascot mood management ──────────────────────────────────────
  useEffect(() => {
    setMascotMood(
      dpgPersonality.getMood({
        isLoading,
        hasUnread,
        isOpen,
        timeSinceLastInteraction: Date.now() - dpgPersonality.lastInteraction,
      }),
    );
  }, [isLoading, hasUnread, isOpen]);

  // ─── Proactive nudge system (Clippy-style pop-ups) ───────────────
  useEffect(() => {
    if (!loggedInUser || isOpen) {
      // Don't nudge while chat is open
      setNudgeVisible(false);
      return;
    }

    // Page-based nudge on page change
    dpgPersonality.setPage(currentPage);
    const pageNudge = dpgPersonality.getPageNudge(currentPage, t);
    if (pageNudge) {
      nudgeTimerRef.current = setTimeout(() => {
        setNudgeBubble(pageNudge.message);
        setMascotMood(pageNudge.mood || "greeting");
        setNudgeVisible(true);
        // Auto-dismiss after 10 seconds
        setTimeout(() => setNudgeVisible(false), 10000);
      }, pageNudge.delay || 5000);
    }

    // Idle nudge timer
    idleTimerRef.current = setInterval(() => {
      if (isOpen) return;
      const idleNudge = dpgPersonality.getIdleNudge(t);
      if (idleNudge) {
        setNudgeBubble(idleNudge.message);
        setMascotMood(idleNudge.mood || "idle");
        setNudgeVisible(true);
        setTimeout(() => setNudgeVisible(false), 10000);
      }
    }, 120000); // Check every 2 minutes

    return () => {
      clearTimeout(nudgeTimerRef.current);
      clearInterval(idleTimerRef.current);
    };
  }, [currentPage, loggedInUser, isOpen, t]);

  // Dismiss nudge when chat opens
  const dismissNudge = useCallback(() => {
    setNudgeVisible(false);
    dpgPersonality.recordInteraction();
  }, []);

  // Speak a message using the configured TTS provider
  const speakText = useCallback((text, msgId) => {
    TTSService.speak(text, {
      onStart: () => setSpeakingMsgId(msgId),
      onEnd: () => setSpeakingMsgId(null),
      onError: () => setSpeakingMsgId(null),
    });
  }, []);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    TTSService.stop();
    setSpeakingMsgId(null);
  }, []);

  // Toggle TTS on/off
  const toggleTts = useCallback(() => {
    setTtsEnabled((prev) => {
      const next = !prev;
      safeStorage.set("dpg-tts-enabled", String(next));
      if (!next) stopSpeaking();
      return next;
    });
  }, [stopSpeaking]);

  // Build user context for API calls
  const getUserContext = useCallback(() => {
    if (!loggedInUser) return {};
    return {
      userUuid: loggedInUser.uuid,
      userName: loggedInUser.firstName || loggedInUser.name,
      subscriptionTier: loggedInUser.subscriptionTier || loggedInUser.planName,
      onboardingComplete: loggedInUser.onboardingComplete || false,
      safetyScore: loggedInUser.safetyScore || null,
    };
  }, [loggedInUser]);

  // Send a message
  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim() || isLoading || !loggedInUser) return;

      // Stop any ongoing speech when user sends a new message
      stopSpeaking();

      // Record interaction for personality engine
      dpgPersonality.recordInteraction();
      setMascotMood("thinking");

      const userMessage = {
        id: Date.now(),
        role: "user",
        content: text.trim(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInputValue("");
      setQuickReplies([]);
      setIsLoading(true);

      try {
        const response = await ChatAPI.sendMessage(
          text.trim(),
          currentPage || "Unknown",
          conversationId,
          getUserContext(),
        );

        if (response.conversationId && !conversationId) {
          setConversationId(response.conversationId);
        }

        const assistantMessage = {
          id: Date.now() + 1,
          role: "assistant",
          content: response.reply,
          suggestedAction: response.suggestedAction,
          suggestedActionLabel: response.suggestedActionLabel,
        };

        setMessages((prev) => [...prev, assistantMessage]);
        setMascotMood("happy");

        // Auto-speak assistant reply when TTS is enabled
        if (ttsEnabled && response.reply) {
          speakText(response.reply, assistantMessage.id);
        }

        if (response.quickReplies && response.quickReplies.length > 0) {
          setQuickReplies(response.quickReplies);
        }
      } catch (error) {
        console.error("Chat error:", error);
        setMascotMood("concerned");
        const errorMessage = {
          id: Date.now() + 1,
          role: "assistant",
          content: t("chat.error.connection"),
        };
        setMessages((prev) => [...prev, errorMessage]);
        setQuickReplies(["Try again", "Contact Support"]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      isLoading,
      loggedInUser,
      currentPage,
      conversationId,
      getUserContext,
      ttsEnabled,
      speakText,
      stopSpeaking,
    ],
  );

  // Handle send button / enter key
  const handleSend = () => {
    sendMessage(inputValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle quick reply click
  const handleQuickReply = (text) => {
    sendMessage(text);
  };

  // Handle suggested action navigation
  const handleAction = (action) => {
    if (setHeroContent && action) {
      setHeroContent(action);
      // Collapse the chat so the user can see the page they were taken to
      setIsOpen(false);
    }
  };

  // Start a new conversation
  const handleNewConversation = async () => {
    if (loggedInUser?.uuid && conversationId) {
      try {
        await ChatAPI.newConversation(loggedInUser.uuid);
      } catch (error) {
        console.error("Error starting new conversation:", error);
      }
    }
    setMessages([]);
    setConversationId(null);
    setQuickReplies([]);
  };

  // Toggle chat open/closed
  const toggleChat = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setHasUnread(false);
      setMascotMood("greeting");
      dismissNudge();
    }
  };

  // Handle starter question click
  const handleStarter = (text) => {
    sendMessage(text);
  };

  // Simple markdown-to-HTML renderer (bold, lists)
  const renderMarkdown = (text) => {
    if (!text) return "";
    let html = text
      // Bold
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      // Unordered list items
      .replace(/^- (.*)/gm, "<li>$1</li>")
      // Wrap consecutive <li> in <ul>
      .replace(/((?:<li>.*<\/li>\s*)+)/g, "<ul>$1</ul>")
      // Line breaks
      .replace(/\n/g, "<br/>");
    return html;
  };

  // Personalized greeting for welcome screen — memoised so it doesn't
  // re-roll a random greeting on every keystroke / re-render.
  const personalGreeting = React.useMemo(
    () => dpgPersonality.getGreeting(loggedInUser?.firstName, t),
    [loggedInUser?.firstName, t],
  );

  // Don't render if not logged in or if the member user has been deactivated
  if (!loggedInUser || isDeactivated) return null;

  const starterQuestions = [
    {
      icon: faEnvelope,
      text: t("chat.starter.email"),
    },
    {
      icon: faGraduationCap,
      text: t("chat.starter.training"),
    },
    {
      icon: faBell,
      text: t("chat.starter.alerts"),
    },
    {
      icon: faQuestionCircle,
      text: t("chat.starter.help"),
    },
  ];

  return (
    <>
      {/* Nudge speech bubble (Clippy-style) */}
      {nudgeVisible && nudgeBubble && !isOpen && (
        <div
          className="dpg-nudge-bubble"
          onClick={() => {
            dismissNudge();
            toggleChat();
          }}
          role="button"
          tabIndex={0}
        >
          <div className="dpg-nudge-content">
            <p>{nudgeBubble}</p>
            <button
              className="dpg-nudge-dismiss"
              onClick={(e) => {
                e.stopPropagation();
                dismissNudge();
              }}
              title={t("chat.nudge.dismiss")}
            >
              &times;
            </button>
          </div>
          <div className="dpg-nudge-tail" />
        </div>
      )}

      {/* Floating Action Button — now with mascot */}
      <div className="chat-fab-container">
        <button
          className={`chat-fab ${nudgeVisible ? "chat-fab--nudging" : ""}`}
          onClick={toggleChat}
          title={isOpen ? t("chat.fab.close") : t("chat.fab.open")}
          aria-label={isOpen ? t("chat.fab.close") : t("chat.fab.ariaOpen")}
        >
          {isOpen ? (
            <FontAwesomeIcon icon={faTimes} />
          ) : (
            <DPGMascot mood={mascotMood} size={42} />
          )}
          {hasUnread && !isOpen && <span className="chat-fab-badge" />}
        </button>
        {!isOpen && (
          <span className="chat-fab-label">{t("chat.fab.label")}</span>
        )}
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div
          className="chat-window"
          role="dialog"
          aria-label={t("chat.window.ariaLabel")}
        >
          {/* Header with mascot */}
          <div className="chat-header">
            <div className="chat-header-left">
              <div className="chat-header-avatar">
                <DPGMascot mood={mascotMood} size={34} />
              </div>
              <div className="chat-header-info">
                <h3>
                  {t("chat.header.name")}{" "}
                  <span className="chat-header-subtitle">
                    {t("chat.header.subtitle")}
                  </span>
                </h3>
                <span className="dpg-status-line">
                  <span className="dpg-status-dot" />
                  {isLoading
                    ? t("chat.header.status.analyzing")
                    : t("chat.header.status.ready")}
                </span>
              </div>
            </div>
            <div className="chat-header-actions">
              <button
                className={`chat-header-btn chat-tts-toggle ${ttsEnabled ? "active" : ""}`}
                onClick={toggleTts}
                title={
                  ttsEnabled ? t("chat.tts.disable") : t("chat.tts.enable")
                }
                aria-label={
                  ttsEnabled
                    ? t("chat.tts.ariaDisable")
                    : t("chat.tts.ariaEnable")
                }
              >
                <FontAwesomeIcon
                  icon={ttsEnabled ? faVolumeUp : faVolumeMute}
                />
              </button>
              <button
                className="chat-header-btn"
                onClick={handleNewConversation}
                title={t("chat.header.newConversation")}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
              <button
                className="chat-header-btn"
                onClick={toggleChat}
                title={t("chat.fab.close")}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          {/* Messages or Welcome */}
          {messages.length === 0 ? (
            <div className="chat-welcome">
              <div className="chat-welcome-header">
                <div className="chat-welcome-mascot">
                  <DPGMascot mood="greeting" size={72} />
                </div>
                <h3>{personalGreeting}</h3>
              </div>
              <p className="chat-welcome-desc">{t("chat.welcome.desc")}</p>
              <div className="chat-welcome-starters">
                {starterQuestions.map((starter, idx) => (
                  <button
                    key={idx}
                    className="chat-starter-btn"
                    onClick={() => handleStarter(starter.text)}
                  >
                    <FontAwesomeIcon icon={starter.icon} />
                    {starter.text}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="chat-messages">
                {messages.map((msg) => (
                  <div key={msg.id} className={`chat-message ${msg.role}`}>
                    <div className="chat-msg-avatar">
                      {msg.role === "assistant" ? (
                        <DPGMascot mood={mascotMood} size={28} />
                      ) : (
                        <FontAwesomeIcon icon={faUser} />
                      )}
                    </div>
                    <div>
                      <div
                        className="chat-msg-bubble"
                        dangerouslySetInnerHTML={{
                          __html: renderMarkdown(msg.content),
                        }}
                      />
                      {msg.role === "assistant" && (
                        <button
                          className={`chat-tts-btn ${speakingMsgId === msg.id ? "speaking" : ""}`}
                          onClick={() =>
                            speakingMsgId === msg.id
                              ? stopSpeaking()
                              : speakText(msg.content, msg.id)
                          }
                          title={
                            speakingMsgId === msg.id
                              ? t("chat.tts.stop")
                              : t("chat.tts.readAloud")
                          }
                          aria-label={
                            speakingMsgId === msg.id
                              ? t("chat.tts.stop")
                              : t("chat.tts.ariaReadAloud")
                          }
                        >
                          <FontAwesomeIcon
                            icon={
                              speakingMsgId === msg.id
                                ? faVolumeOff
                                : faVolumeUp
                            }
                          />
                        </button>
                      )}
                      {msg.suggestedAction && msg.suggestedActionLabel && (
                        <button
                          className="chat-msg-action"
                          onClick={() => handleAction(msg.suggestedAction)}
                        >
                          {msg.suggestedActionLabel}
                          <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Typing indicator */}
                {isLoading && (
                  <div className="chat-typing">
                    <div className="chat-typing-dots">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Quick Replies */}
              {quickReplies.length > 0 && !isLoading && (
                <div className="chat-quick-replies">
                  {quickReplies.map((reply, idx) => (
                    <button
                      key={idx}
                      className="chat-quick-reply"
                      onClick={() => handleQuickReply(reply)}
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Input Area */}
          <div className="chat-input-area">
            <textarea
              ref={inputRef}
              className="chat-input"
              placeholder={t("chat.input.placeholder")}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            <button
              className="chat-send-btn"
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
              title={t("chat.send.title")}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>

          {/* Footer */}
          <div className="chat-footer">
            <DPGMascot mood="idle" size={14} className="chat-footer-mascot" />
            {t("chat.footer.powered")}
          </div>
        </div>
      )}
    </>
  );
}

export default ChatWidget;
